import { useState, useEffect, useRef, createContext, useContext, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { RouteSeo } from "./components/RouteSeo";
import { setInsightsEnabled, installNavTracking, trackView } from "./insights";

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E5C55] mx-auto mb-4"></div>
      <p className="text-[#5C5C5C]">Loading...</p>
    </div>
  </div>
);

// Critical pages loaded immediately (for fast initial load)
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import { TestLabChip } from "./components/admin/TestLabChip";
import { RandomUserBanner } from "./components/admin/RandomUserBanner";
import { PlayerProvider } from "./player/PlayerContext";
import { FloatingPlayer } from "./player/FloatingPlayer";
import { RadioProvider } from "./radio/RadioContext";
import { RadioDock } from "./radio/RadioDock";
import { HoardProvider, HoardDock } from "./components/hoard/HoardDock";
import { KrakonProvider, KrakonDock } from "./components/krakon/KrakonDock";
import { AuditionPlayer } from "./components/vault/AuditionReel";
import { HearPinsLayer } from "./radio/HearPins";
import { LanguageProvider } from "./i18n/LanguageContext";
const NEWS_HOST = /(^|\.)thewall\.news$/i.test(window.location.hostname);
import { FloatingClaim } from "./components/FloatingClaim";
import { VerifyEmailNudge } from "./components/VerifyEmailNudge";

// Lazy load other pages (loaded on demand)
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const NewHomePage = lazy(() => import("./pages/NewHomePage"));
const HomePage2026 = lazy(() => import("./pages/HomePage2026"));
const SafetyGuidePage = lazy(() => import("./pages/SafetyGuidePage"));
const TestingPage = lazy(() => import("./pages/TestingPage"));
const CausePage = lazy(() => import("./pages/CausePage"));
const PorchfestPage = lazy(() => import("./pages/PorchfestPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ContributorPortal = lazy(() => import("./pages/ContributorPortal"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const TroveGallery = lazy(() => import("./pages/TroveGallery"));
const OrganizationDetail = lazy(() => import("./pages/OrganizationDetail"));
const SubmitReceipt = lazy(() => import("./pages/SubmitReceipt"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const OriginsPage = lazy(() => import("./pages/OriginsPage"));
const ContributePage = lazy(() => import("./pages/ContributePage"));
const MyTourPage = lazy(() => import("./pages/MyTourPage"));
const ManageHubPage = lazy(() => import("./pages/ManageHubPage"));
const EpkBuilderPage = lazy(() => import("./pages/EpkBuilderPage"));
const InvitePage = lazy(() => import("./pages/InvitePage"));
const MusiciansLandingPage = lazy(() => import("./pages/MusiciansLandingPage"));
const FoodResourcesPage = lazy(() => import("./pages/FoodResourcesPage"));
const PartnerSkoolieValueProp = lazy(() => import("./pages/PartnerSkoolieValueProp"));
const SkoolieFeaturePage = lazy(() => import("./pages/SkoolieFeaturePage"));
const OutsidersGuidePage = lazy(() => import("./pages/OutsidersGuidePage"));
const PartnerImaginationLibrary = lazy(() => import("./pages/PartnerImaginationLibrary"));
const SoundLifePage = lazy(() => import("./pages/SoundLifePage"));
const SoundLifeHubPage = lazy(() => import("./pages/SoundLifeHubPage"));
const ForBandsPage = lazy(() => import("./pages/ForBandsPage"));
const MusicianDashboard = lazy(() => import("./pages/MusicianDashboard"));
const VoltBoxPage = lazy(() => import("./pages/VoltBoxPage"));
const VoltBoxSetPage = lazy(() => import("./pages/VoltBoxSetPage"));
const VaultPreviewPage = lazy(() => import("./pages/VaultPreviewPage"));
const FanStudioPage = lazy(() => import("./pages/FanStudioPage"));
const FanUploadsPage = lazy(() => import("./pages/FanUploadsPage"));
const FanReviewPage = lazy(() => import("./pages/FanReviewPage"));
const ForBusinessesPage = lazy(() => import("./pages/ForBusinessesPage"));
const ForOrgsPage = lazy(() => import("./pages/ForOrgsPage"));
const OrgBuildPage = lazy(() => import("./pages/OrgBuildPage"));
const RecommendOrg = lazy(() => import("./pages/RecommendOrg"));
const MapPage = lazy(() => import("./pages/MapPage"));
const ArtistsPage = lazy(() => import("./pages/ArtistsPage"));
const ArtistProfilePage = lazy(() => import("./pages/ArtistProfilePage"));
const ArtistTourPage = lazy(() => import("./pages/ArtistTourPage"));
const ArtistEditPage = lazy(() => import("./pages/ArtistEditPage"));
const BusinessProfilePage = lazy(() => import("./pages/BusinessProfilePage"));
const BusinessProfile = lazy(() => import("./pages/BusinessProfile"));
const ClaimBusinessPage = lazy(() => import("./pages/ClaimBusinessPage"));
const MyBusinessPage = lazy(() => import("./pages/MyBusinessPage"));
const MyOrgPage = lazy(() => import("./components/krakon/OrgWishlist"));
const StargatePage = lazy(() => import("./pages/StargatePage"));
const TroveGuide = lazy(() => import("./pages/TroveGuide"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const ContributeResource = lazy(() => import("./pages/ContributeResource"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const DisclaimerPage = lazy(() => import("./pages/DisclaimerPage"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const CopyrightPage = lazy(() => import("./pages/CopyrightPage"));
const AiPage = lazy(() => import("./pages/AiPage"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AbuseHub = lazy(() => import("./pages/AbuseHub"));
const RedirectKeepHash = ({ to }) => { const { hash } = useLocation(); return <Navigate to={`${to}${hash}`} replace />; };
const EventsCalendar = lazy(() => import("./pages/EventsCalendar"));
const MusicScene = lazy(() => import("./pages/MusicScene"));
const TrailPage = lazy(() => import("./pages/TrailPage"));
const EventDetailPage = lazy(() => import("./pages/EventDetailPage"));
const SuggestEventPage = lazy(() => import("./pages/SuggestEventPage"));
const GigHarborPage = lazy(() => import("./pages/GigHarborPage"));
const CityGuidePage = lazy(() => import("./pages/CityGuidePage"));
const WindowPage = lazy(() => import("./pages/WindowPage"));
const PartnerWindowPage = lazy(() => import("./pages/PartnerWindowPage"));
const HolidayHopPage = lazy(() => import("./pages/HolidayHopPage"));
const SantaParadePage = lazy(() => import("./pages/SantaParadePage"));
const HolidayHopAdminPage = lazy(() => import("./pages/HolidayHopAdminPage"));
const AdminOutreachPage = lazy(() => import("./pages/AdminOutreachPage"));
const AdminWelcomeTourPage = lazy(() => import("./pages/AdminWelcomeTourPage"));
const AdminTestLabPage = lazy(() => import("./pages/AdminTestLabPage"));
const AdminMigrationsPage = lazy(() => import("./pages/AdminMigrationsPage"));
const PassportPage = lazy(() => import("./pages/PassportPage"));
const HelpBuildPage = lazy(() => import("./pages/HelpBuildPage"));
const UnderwritePage = lazy(() => import("./pages/UnderwritePage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentResultPage").then((m) => ({ default: m.PaymentSuccessPage })));
const PaymentCancelPage = lazy(() => import("./pages/PaymentResultPage").then((m) => ({ default: m.PaymentCancelPage })));
const RedemptionPage = lazy(() => import("./pages/RedemptionPage"));
const GetInvolvedPage = lazy(() => import("./pages/GetInvolvedPage"));
const ReferBusinessPage = lazy(() => import("./pages/ReferBusinessPage"));
const MusicCalendar = lazy(() => import("./pages/MusicCalendar"));
const FeaturedArtistsPage = lazy(() => import("./pages/FeaturedArtistsPage"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const NightPage = lazy(() => import("./pages/NightPage"));
const NewsPage = lazy(() => import("./pages/NewsPage"));
const EpisodePage = lazy(() => import("./pages/EpisodePage"));
const SharePage = lazy(() => import("./pages/SharePage"));
const StudioPage = lazy(() => import("./pages/StudioPage"));
const RecordPage = lazy(() => import("./pages/RecordPage"));
const SamplerPage = lazy(() => import("./pages/SamplerPage"));
const KrakenSharePage = lazy(() => import("./pages/KrakenSharePage"));
const VideoDeskPage = lazy(() => import("./pages/VideoDeskPage"));
const VoltDeskPage = lazy(() => import("./pages/VoltDeskPage"));
const SounDoorPage = lazy(() => import("./pages/SounDoorPage"));
const RadioAdminPage = lazy(() => import("./pages/RadioAdminPage"));
const ListenRedirect = lazy(() => import("./radio/MomentLink"));
const BandsLandingPage = lazy(() => import("./pages/BandsLandingPage"));
const BandTourPage = lazy(() => import("./pages/BandTourPage"));
const AdminBulkImport = lazy(() => import("./pages/AdminBulkImport"));
const CommunityHubPage = lazy(() => import("./pages/CommunityHubPage"));
const OnScreenPage = lazy(() => import("./pages/OnScreenPage"));
const FestivalPage = lazy(() => import("./pages/FestivalPage"));
const TwelvePage = lazy(() => import("./pages/TwelvePage"));
const ShopApp = lazy(() => import("./shop/ShopApp"));

// Catch-all: a single-segment path may be an admin-picked short path for a Page Builder page (e.g. /housing).
const ShortPathOrHome = () => {
  const { pathname } = useLocation();
  const seg = pathname.replace(/^\/+|\/+$/g, "");
  const [state, setState] = useState("checking");
  // THEWALL.news front door: the bare domain opens on the News page.
  const newsHost = NEWS_HOST;
  useEffect(() => {
    if (!seg || seg.includes("/")) { setState("home"); return; }
    let cancelled = false;
    axios.get(`${API}/festivals/${encodeURIComponent(seg)}`, { withCredentials: true })
      .then(() => !cancelled && setState("page"))
      .catch(() => !cancelled && setState("home"));
    return () => { cancelled = true; };
  }, [seg]);
  if (state === "checking") return <PageLoader />;
  if (state === "page") return <FestivalPage slugOverride={seg} />;
  if (newsHost && !seg) return <Navigate to="/news" replace />;
  return <Navigate to="/" replace />;
};
const BuildsPage = lazy(() => import("./pages/BuildsPage"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ============ API ERROR HANDLING ============
// Axios request interceptor - attach localStorage token for auth + magician mode header
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // If admin has flipped the "Magician Mode" toggle in the Admin Panel,
  // tag every outbound request so the backend marks created content as magician-attributed.
  if (localStorage.getItem("magicianMode") === "true") {
    config.headers["X-Magician-Mode"] = "true";
  }
  return config;
});

// Global axios interceptor for graceful error handling
// Track failed requests to avoid showing errors on brief network hiccups
let networkErrorCount = 0;
let networkErrorTimeout = null;

axios.interceptors.response.use(
  (response) => {
    // Reset error count on successful response
    networkErrorCount = 0;
    if (networkErrorTimeout) {
      clearTimeout(networkErrorTimeout);
      networkErrorTimeout = null;
    }
    return response;
  },
  (error) => {
    // Uniform "verify your email" prompt: any 403 carrying the VERIFY_EMAIL_REQUIRED marker opens the nudge.
    if (error.response?.status === 403 && String(error.response?.data?.detail || "").startsWith("VERIFY_EMAIL_REQUIRED")) {
      window.dispatchEvent(new CustomEvent("wall:verify-required"));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    // Handle 401 - clear stale token so user can re-login cleanly
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Don't clear token for login/register attempts (those are expected 401s for bad credentials)
      if (!url.includes('/auth/login') && !url.includes('/auth/register')) {
        // Token is expired or invalid — only clear if we actually had one
        const hadToken = localStorage.getItem("token");
        if (hadToken && url.includes('/auth/me')) {
          localStorage.removeItem("token");
        }
      }
    }
    // Handle network errors (backend unreachable)
    else if (!error.response) {
      networkErrorCount++;
      // Network error - backend may be unavailable
      
      // Only show error toast after multiple failures (3+) to avoid false alarms on page load
      // This gives the backend time to wake up on cold starts
      if (networkErrorCount >= 3 && !window._backendErrorShown) {
        // Add a small delay to see if subsequent requests succeed
        if (!networkErrorTimeout) {
          networkErrorTimeout = setTimeout(() => {
            if (networkErrorCount >= 3 && !window._backendErrorShown) {
              toast.error("Unable to connect to server. Some features may be unavailable.");
              window._backendErrorShown = true;
              // Reset after 60 seconds to allow showing error again if it persists
              setTimeout(() => { window._backendErrorShown = false; }, 60000);
            }
            networkErrorTimeout = null;
          }, 3000); // Wait 3 seconds before showing error
        }
      }
    }
    // Handle 5xx server errors - with delay to avoid initial load false alarms
    else if (error.response.status >= 500) {
      // Server error logged
      networkErrorCount++;
      
      // Only show error after multiple 500s and with a delay
      if (networkErrorCount >= 2 && !window._serverErrorShown) {
        if (!networkErrorTimeout) {
          networkErrorTimeout = setTimeout(() => {
            if (networkErrorCount >= 2 && !window._serverErrorShown) {
              toast.error("Server error. Please try again later.");
              window._serverErrorShown = true;
              setTimeout(() => { window._serverErrorShown = false; }, 30000);
            }
            networkErrorTimeout = null;
          }, 3000); // Wait 3 seconds before showing server error
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Stable callback that any consumer can use to force a re-check after auth
  // events (login from another tab, token refresh, etc.).
  const randomUserMode = localStorage.getItem("wall_random_user_mode");  // "1" = new user · "donor" = unlocked-Hoard User View
  if (randomUserMode === "1" || randomUserMode === "donor") axios.defaults.headers.common["X-Random-User"] = randomUserMode;
  else delete axios.defaults.headers.common["X-Random-User"];

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (!cancelled) setUser(response.data);
      } catch (error) {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = (userData, token) => {
    setUser(userData);
    if (token) {
      localStorage.setItem("token", token);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Logout error handled silently
    }
    setUser(null);
    localStorage.removeItem("token");
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E5C55]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();
  const { user, loading } = useAuth();

  // Anonymous journey insights: on after auth settles, never for admins/magicians.
  useEffect(() => {
    if (loading) return;
    const on = !["admin", "magician"].includes(user?.role);
    setInsightsEnabled(on);
    if (on) installNavTracking();
  }, [loading, user?.role]);
  useEffect(() => { if (!loading) trackView(location.pathname); }, [loading, location.pathname]);

  // Scroll to top on route change, or scroll to hash if present
  useEffect(() => {
    if (location.hash) {
      // Wait a tick for the page to render, then scroll to the element
      setTimeout(() => {
        const element = document.getElementById(location.hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // Scroll to top on regular navigation
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [location.pathname, location.hash]);

  // Check URL fragment for session_id BEFORE rendering routes (prevents race conditions)
  if (location.hash?.includes('session_id=')) {
    return <Suspense fallback={<PageLoader />}><AuthCallback /></Suspense>;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <RouteSeo />
      <VerifyEmailNudge />
      <Routes>
        <Route path="/" element={NEWS_HOST ? <Navigate to="/news" replace /> : <LandingPage />} />
        <Route path="/new-home" element={<NewHomePage />} />
        <Route path="/home-new" element={<HomePage2026 />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/our-lineage" element={<Navigate to="/our-origins" replace />} />
        <Route path="/lineage" element={<Navigate to="/our-origins" replace />} />
        <Route path="/origins" element={<OriginsPage />} />
        <Route path="/outsiders-guide" element={<OutsidersGuidePage />} />
        <Route path="/partners/skoolie" element={<FestivalPage slugOverride="skoolie" />} />
        <Route path="/skoolie" element={<FestivalPage slugOverride="skoolie" />} />
        <Route path="/music-and-heart-in-wright-park" element={<FestivalPage slugOverride="music-and-heart" />} />
        <Route path="/music-and-heart" element={<FestivalPage slugOverride="music-and-heart" />} />
        <Route path="/skoolie/classic" element={<SkoolieFeaturePage />} />
        <Route path="/partners/skoolie/proposal" element={<PartnerSkoolieValueProp />} />
        <Route path="/partners/imagination-library" element={<PartnerImaginationLibrary />} />
        <Route path="/SoundLife" element={<SoundLifeHubPage />} />
        <Route path="/soundlife" element={<SoundLifeHubPage />} />
        <Route path="/SoundLifeHub" element={<SoundLifeHubPage />} />
        <Route path="/soundlifehub" element={<SoundLifeHubPage />} />
        <Route path="/SoundLifeLegacy" element={<SoundLifePage />} />
        <Route path="/soundlifelegacy" element={<SoundLifePage />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/artists" element={<ArtistsPage />} />
        <Route path="/artist/:userId" element={<ArtistProfilePage />} />
        {/* /artists/:slug + /bands/:slug both serve the unified BandTourPage.
            ArtistTourPage is the legacy single-tier page; kept around only as
            a fallback in case we need to revert. New canonical: BandTourPage
            (which progressively reveals tier 0→3 features). */}
        <Route path="/artists/:slug" element={<BandTourPage />} />
        <Route path="/artists/:slug/edit" element={<ArtistEditPage />} />
        <Route path="/business/:odId" element={<BusinessProfilePage />} />
        <Route path="/business/dir/:businessId" element={<BusinessProfile user={user} />} />
        <Route path="/claim" element={<ClaimBusinessPage />} />
        <Route path="/my-business" element={<MyBusinessPage />} />
        <Route path="/my-org" element={<MyOrgPage />} />
        <Route path="/stargate" element={<StargatePage />} />
        <Route path="/trove-guide" element={<TroveGuide user={user} />} />
        <Route path="/hoard-guide" element={<TroveGuide user={user} />} />
        <Route path="/events" element={<EventsCalendar />} />
        <Route path="/porchfest" element={<PorchfestPage />} />
        <Route path="/tacomaporchfest" element={<PorchfestPage />} />
        {/* Combined Community Hub — Maps + Calendars + Local Gems stacked in
            a single page. Standalone /events, /trove-guide, /deals still exist. */}
        <Route path="/community-hub" element={<CommunityHubPage />} />
        <Route path="/First-Star-Entertainment" element={<OnScreenPage slug="first-star" />} />
        <Route path="/first-star-entertainment" element={<OnScreenPage slug="first-star" />} />
        <Route path="/The-Red-Head" element={<OnScreenPage slug="the-red-head" />} />
        <Route path="/the-red-head" element={<OnScreenPage slug="the-red-head" />} />
        <Route path="/OnScreen/:slug" element={<OnScreenPage />} />
        <Route path="/onscreen/:slug" element={<OnScreenPage />} />
        <Route path="/communityhub" element={<CommunityHubPage />} />
        <Route path="/maps-calendars" element={<CommunityHubPage />} />
        {/* Festival Build v1: canonical URL + admin-picked optional short_paths.
            Short-paths are resolved by the FestivalPage itself against the API
            (which accepts either slug or short_path), so we only need one route
            plus a catch-all wildcard the router uses as a last resort. */}
        <Route path="/festival/:slug" element={<FestivalPage />} />
        <Route path="/artist/page/:slug" element={<FestivalPage />} />
        <Route path="/org/:slug" element={<FestivalPage />} />
        <Route path="/business/page/:slug" element={<FestivalPage />} />
        <Route path="/person/:slug" element={<FestivalPage />} />
        <Route path="/guide/:slug" element={<FestivalPage />} />
        <Route path="/resource/:slug" element={<FestivalPage />} />
        <Route path="/example/:slug" element={<FestivalPage />} />
        <Route path="/tacomasantaparade" element={<FestivalPage slugOverride="tacomasantaparade" />} />
        <Route path="/peaceday" element={<FestivalPage slugOverride="peaceday" />} />
        <Route path="/gather" element={<FestivalPage slugOverride="gather" />} />
        <Route path="/sup" element={<FestivalPage slugOverride="gather" />} />
        <Route path="/gathergh" element={<FestivalPage slugOverride="gather-gig-harbor" />} />
        <Route path="/gem-demo" element={<FestivalPage slugOverride="gem-demo" />} />
        <Route path="/demo" element={<FestivalPage slugOverride="gem-demo" />} />
        <Route path="/give" element={<FestivalPage slugOverride="giving-map" />} />
        <Route path="/traditions" element={<FestivalPage slugOverride="traditions" />} />
        <Route path="/radiotacoma" element={<FestivalPage slugOverride="radio-tacoma" />} />
        <Route path="/framemakers" element={<FestivalPage slugOverride="tacoma-frame-makers" />} />
        <Route path="/olalla-bay" element={<FestivalPage slugOverride="olalla-bay-market" />} />
        <Route path="/olalla" element={<FestivalPage slugOverride="olalla-bay-market" />} />
        <Route path="/hellojude" element={<FestivalPage slugOverride="hellojude" />} />
        <Route path="/toys" element={<FestivalPage slugOverride="toys-for-everyone" />} />
        <Route path="/stephan" element={<FestivalPage slugOverride="stephan-childers" />} />
        <Route path="/shop/*" element={<ShopApp />} />
        <Route path="/admin/shop" element={<RedirectKeepHash to="/shop/admin" />} />
        <Route path="/help-build" element={<HelpBuildPage />} />
        <Route path="/underwrite" element={<UnderwritePage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/cancel" element={<PaymentCancelPage />} />
        <Route path="/cannabis" element={<FestivalPage slugOverride="cannabis" />} />
        <Route path="/12" element={<TwelvePage />} />
        <Route path="/soberaf" element={<FestivalPage slugOverride="sober-af" />} />
        <Route path="/builds" element={<BuildsPage />} />
        <Route path="/julia" element={<FestivalPage slugOverride="julia-josephine" />} />
        <Route path="/champagne-sunday" element={<FestivalPage slugOverride="champagne-sunday" />} />
        <Route path="/giving-map" element={<FestivalPage slugOverride="giving-map" />} />
        <Route path="/sunny" element={<FestivalPage slugOverride="sunny-loudin" />} />
        <Route path="/sunny-loudin" element={<FestivalPage slugOverride="sunny-loudin" />} />
        <Route path="/weinthe253" element={<FestivalPage slugOverride="weinthe253" />} />
        <Route path="/our-origins" element={<FestivalPage slugOverride="our-origins" />} />
        <Route path="/dain-norman" element={<FestivalPage slugOverride="dain-norman" />} />
        <Route path="/snapshot" element={<FestivalPage slugOverride="dain-norman" />} />
        <Route path="/events/music" element={<MusicScene />} />
        <Route path="/event/:eventId" element={<EventDetailPage />} />
        <Route path="/events/suggest" element={<SuggestEventPage />} />
        <Route path="/trail/:slug" element={<TrailPage />} />
        {/* Constellation routes (new public-facing name for Event Trails — astral theme) */}
        <Route path="/constellation/:slug" element={<TrailPage />} />
        {/* Vanity URLs for memorable, marketable event-constellation pages */}
        <Route path="/tacomajoy" element={<TrailPage slug="tacoma-joy-2026" />} />
        {/* City directory pages (chamber-sourced) */}
        <Route path="/gig-harbor" element={<GigHarborPage />} />
        <Route path="/gigharbor" element={<GigHarborPage />} />
        {/* Neighborhood guides under Tacoma */}
        <Route path="/tacoma/6th-ave" element={<CityGuidePage slug="tacoma-6th-ave" />} />
        <Route path="/6thave" element={<CityGuidePage slug="tacoma-6th-ave" />} />
        <Route path="/sixthave" element={<CityGuidePage slug="tacoma-6th-ave" />} />
        <Route path="/tacoma/south-tacoma-way" element={<CityGuidePage slug="tacoma-south-tacoma-way" />} />
        <Route path="/stw" element={<CityGuidePage slug="tacoma-south-tacoma-way" />} />
        <Route path="/tacoma/stw" element={<CityGuidePage slug="tacoma-south-tacoma-way" />} />
        {/* Thematic "Treasure Map" portals — non-district guides */}
        <Route path="/tacoma/parks" element={<CityGuidePage slug="tacoma-parks" />} />
        <Route path="/parks" element={<CityGuidePage slug="tacoma-parks" />} />
        {/* WINDOW — partner orgs use /partners/:slug (Skoolie + future
            partners). Legacy /window/:slug remains for curated single-
            business pages (Sunset Mart, etc.) — different content type,
            different URL family. */}
        <Route path="/window/:slug" element={<WindowPage />} />
        <Route path="/partners/:slug" element={<PartnerWindowPage />} />
        {/* Holiday HOPE HOP — Winter Solstice / Tacoma Santa Parade
            community-business activation. /hop, /holiday-hop, and
            /holiday-hope-hop are all aliases so any flyer or QR works. */}
        <Route path="/holiday-hop" element={<HolidayHopPage />} />
        <Route path="/hop" element={<HolidayHopPage />} />
        <Route path="/holiday-hope-hop" element={<HolidayHopPage />} />
        {/* Tacoma Santa Parade detail page — canonical URL is
            /tacoma-santa-parade. /santa-parade kept as alias for any
            already-shared links. */}
        <Route path="/tacoma-santa-parade" element={<SantaParadePage />} />
        <Route path="/santa-parade" element={<SantaParadePage />} />
        <Route path="/santaparade" element={<SantaParadePage />} />
        <Route path="/admin/testlab" element={<ProtectedRoute adminOnly><AdminTestLabPage /></ProtectedRoute>} />
        <Route path="/admin/ws1-migration" element={<Navigate to="/admin/migrations/ws1" replace />} />
        <Route path="/admin/migrations/:name" element={<ProtectedRoute adminOnly><AdminMigrationsPage /></ProtectedRoute>} />
        <Route
          path="/admin/outreach"
          element={
            <ProtectedRoute adminOnly>
              <AdminOutreachPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/welcome-tour"
          element={
            <ProtectedRoute adminOnly>
              <AdminWelcomeTourPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/holiday-hop"
          element={
            <ProtectedRoute adminOnly>
              <HolidayHopAdminPage />
            </ProtectedRoute>
          }
        />
        {/* HOARD (code: passport) — own view requires sign-in; the
            business redemption page is publicly accessible (the passport
            code itself is the credential). */}
        <Route path="/starport" element={<Navigate to="/passport" replace />} />
        <Route path="/hoard" element={<Navigate to="/passport" replace />} />
        <Route path="/music" element={<Navigate to="/passport" replace />} />
        <Route path="/art" element={<Navigate to="/passport" replace />} />
        <Route path="/deals" element={<Navigate to="/passport" replace />} />
        <Route
          path="/passport"
          element={
            <PassportPage />
          }
        />
        <Route path="/redeem" element={<RedemptionPage />} />
        {/* Get Involved — volunteer opportunities */}
        <Route path="/get-involved" element={<GetInvolvedPage />} />
        {/* Refer a business — public Treasure Queue intake */}
        <Route path="/refer-business" element={<ReferBusinessPage />} />
        <Route path="/refer" element={<ReferBusinessPage />} />
        {/* Music portal — dedicated music-map + flyer-wall page + featured artists */}
        <Route path="/music/map" element={<MusicCalendar />} />
        <Route path="/music/calendar" element={<MusicCalendar />} />
        <Route path="/artists/featured" element={<FeaturedArtistsPage />} />
        <Route path="/featured-artists" element={<FeaturedArtistsPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/night" element={<NightPage />} />
        <Route path="/123" element={<HowItWorksPage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/news/:slug" element={<EpisodePage />} />
        <Route path="/admin/studio" element={<StudioPage />} />
        <Route path="/record" element={<Suspense fallback={<div />}><RecordPage /></Suspense>} />
        <Route path="/sampler" element={<Suspense fallback={<div />}><SamplerPage /></Suspense>} />
        <Route path="/:shareSlug/kraken" element={<Suspense fallback={<div />}><KrakenSharePage /></Suspense>} />
        <Route path="/admin/voltbox" element={<Suspense fallback={<div />}><VoltBoxPage /></Suspense>} />
        <Route path="/admin/voltbox/:itemId" element={<Suspense fallback={<div />}><VoltBoxSetPage /></Suspense>} />
        <Route path="/vault/preview/:token" element={<VaultPreviewPage />} />
        <Route path="/fan-studio" element={<Suspense fallback={<div />}><FanStudioPage /></Suspense>} />
        <Route path="/admin/fan-uploads" element={<Suspense fallback={<div />}><FanUploadsPage /></Suspense>} />
        <Route path="/admin/fan-uploads/:subId" element={<Suspense fallback={<div />}><FanReviewPage /></Suspense>} />
        <Route path="/admin/video" element={<VideoDeskPage />} />
        <Route path="/admin/volt" element={<VoltDeskPage />} />
        <Route path="/soundoor" element={<SounDoorPage />} />
        <Route path="/admin/soundoor" element={<SounDoorPage />} />
        <Route path="/admin/radio" element={<RadioAdminPage />} />
        <Route path="/listen/:key" element={<ListenRedirect />} />
        <Route path="/s/:shareId" element={<SharePage />} />
        <Route path="/bands" element={<BandsLandingPage />} />
        <Route path="/bands/:slug" element={<BandTourPage />} />
        <Route path="/lozen" element={<Navigate to="/bands/lozen" replace />} />
        <Route path="/organizations/:orgId" element={<OrganizationDetail />} />
        <Route path="/trove" element={<TroveGallery />} />
        <Route path="/hoard" element={<TroveGallery />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/abuse" element={<AbuseHub />} />
        <Route path="/understanding-abuse" element={<RedirectKeepHash to="/abuse" />} />
        <Route path="/causes/:slug" element={<CausePage />} />
        <Route path="/drinks" element={<CausePage slugOverride="drink-spiking" />} />
        <Route path="/safety" element={<SafetyGuidePage />} />
        <Route path="/safety/testing" element={<TestingPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/copyright" element={<CopyrightPage />} />
        <Route path="/ai" element={<AiPage />} />
        <Route path="/disclaimer" element={<DisclaimerPage />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route
          path="/contribute-resource"
          element={
            <ProtectedRoute>
              <ContributeResource />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <Dashboard />
          }
        />
        <Route
          path="/contribute"
          element={<ContributePage />}
        />
        <Route
          path="/contribute/upload"
          element={
          <ProtectedRoute>
            <ContributorPortal />
          </ProtectedRoute>
        }
      />
        <Route
          path="/my-tour"
          element={
            <ProtectedRoute>
              <MyTourPage />
            </ProtectedRoute>
          }
        />
        <Route path="/manage" element={<ProtectedRoute><ManageHubPage /></ProtectedRoute>} />
        <Route path="/manage/epk/:slug" element={<ProtectedRoute><EpkBuilderPage /></ProtectedRoute>} />
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/musicians" element={<MusiciansLandingPage />} />
        <Route path="/food-nourishment-at-our-roots" element={<FoodResourcesPage />} />
        <Route path="/for-bands" element={<ForBandsPage />} />
        <Route path="/musician/:sessionId" element={<MusicianDashboard />} />
        <Route path="/musician" element={<MusicianDashboard />} />
        <Route path="/for-businesses" element={<ForBusinessesPage />} />
        <Route path="/for-orgs" element={<ForOrgsPage />} />
        <Route path="/orgs/build" element={<OrgBuildPage user={user} />} />
      <Route
        path="/submit-receipt"
        element={
          <ProtectedRoute>
            <SubmitReceipt />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recommend-org"
        element={
          <ProtectedRoute>
            <RecommendOrg />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminPanel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/bulk-import"
        element={
          <ProtectedRoute adminOnly>
            <AdminBulkImport />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<ShortPathOrHome />} />
      </Routes>
      {!location.pathname.startsWith("/vault/preview") && !/^\/[^/]+\/kraken\/?$/.test(location.pathname) && (
        <>
          <TestLabChip user={user} />
          <RandomUserBanner user={user} />
          <FloatingPlayer />
          <RadioDock />
          <HoardDock />
          <KrakonDock />
          {!location.pathname.startsWith("/admin/voltbox") && !location.pathname.startsWith("/record") && !location.pathname.startsWith("/sampler") && <AuditionPlayer />}
          <HearPinsLayer />
          <FloatingClaim />
        </>
      )}
    </Suspense>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <LanguageProvider>
        <PlayerProvider>
          <RadioProvider>
            <HoardProvider>
            <KrakonProvider>
            <AppRouter />
            </KrakonProvider>
            </HoardProvider>
          </RadioProvider>
        </PlayerProvider>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
